// ============================================================================
// ActiveCampaignBanner.tsx — F3 envío inteligente: progreso visible al entrar
//
// Suscripción en vivo (onSnapshot) a las campañas activas del negocio con
// sendPlan en curso. Como el worker actualiza sendPlan.totalSent en cada
// envío, la barra avanza en tiempo real sin polling.
//
// variant='global' → franja compacta en el Layout (todas las páginas, se
//   oculta en /dashboard/campaigns y se puede cerrar por sesión).
// variant='page'   → versión detallada arriba de la lista en CampaignsPage
//   (una card por campaña activa, no se puede cerrar).
//
// La query reusa el patrón businessId+status de CampaignsPage (sin índices
// nuevos); el filtro por sendPlan.status se hace en cliente.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CalendarClock, X } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
// Re-cableado a lealify-base (Inc10): progreso por polling a /campaigns (el worker
// actualiza total_sent en base). Reemplaza el onSnapshot a Firestore.
import { campaignsApi } from '../../../api/campaignsApi';
import type { SendPlan } from '../types/campaign';
import { chipEnvio } from '../campaignVocab';

const ACTIVE_STATUSES = ['scheduled', 'running', 'paused', 'paused_error'];
const DISMISS_KEY = 'activeCampaignBannerDismissed';

interface ActiveCampaign {
  id: string;
  name: string;
  mode?: 'fixed' | 'dynamic';   // dinámica = regla viva (sin ETA de término)
  sendPlan: SendPlan & { sentTodayCount?: number; sentTodayDate?: string };
}

function pct(plan: SendPlan): number {
  if (!plan.totalQueued) return 0;
  return Math.min(100, Math.round((plan.totalSent / plan.totalQueued) * 100));
}

function sentToday(plan: ActiveCampaign['sendPlan']): number {
  // El backend calcula sentTodayCount fresco por request (DATE(sent_at)=hoy, igual que el
  // cupo del worker), así que se usa directo — antes dependía de una fecha que no cuadraba
  // (tz/campo sin poblar) y mostraba "hoy 0" aunque hubiera envíos.
  return plan.sentTodayCount ?? 0;
}

// ETA en vivo: pendientes / ritmo, escalado por días activos a la semana
function etaLabel(plan: SendPlan): string {
  const remaining = Math.max(0, (plan.totalQueued || 0) - (plan.totalSent || 0));
  if (!remaining || !plan.msgsPerDay) return '';
  const sendDays = Math.ceil(remaining / plan.msgsPerDay);
  const activeDays = plan.weekdays?.length || 5;
  const calendarDays = Math.ceil(sendDays * (7 / activeDays));
  const end = new Date();
  end.setDate(end.getDate() + calendarDays);
  return end.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

// Mismas palabras que la lista y que la agenda (vocabulario común, F3). Antes este banner
// decía "En pausa" donde la lista decía "Pausada", y llamaba igual a una pausa a propósito
// que a una detenida por errores.
const statusLabel = (status: string): string => chipEnvio(status)?.label || 'Pausada';

const ActiveCampaignBanner: React.FC<{ variant?: 'global' | 'page' }> = ({ variant = 'global' }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [campaigns, setCampaigns] = useState<ActiveCampaign[]>([]);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');

  useEffect(() => {
    const businessId = currentUser?.businessId;
    if (!businessId) return;
    let alive = true;
    const load = async () => {
      try {
        const list = await campaignsApi.list();
        if (!alive) return;
        const actives: ActiveCampaign[] = list
          // Las archivadas NO salen aquí: están fuera de circulación y anunciarlas como
          // "campaña en marcha" es justo la contradicción que F2/F3 vienen a quitar.
          .filter((c) => !c.archivedAt && c.sendPlan && ACTIVE_STATUSES.includes(c.sendPlan.status))
          .map((c) => ({ id: c.id, name: c.name || 'Campaña', mode: c.mode, sendPlan: c.sendPlan as ActiveCampaign['sendPlan'] }));
        setCampaigns(actives);
      } catch (err: any) {
        console.warn('ActiveCampaignBanner: error', err?.message);
      }
    };
    load();
    const t = setInterval(load, 15000);   // refresca el progreso cada 15s
    return () => { alive = false; clearInterval(t); };
  }, [currentUser?.businessId]);

  if (variant === 'global' && (dismissed || location.pathname.startsWith('/dashboard/campaigns'))) return null;
  if (!campaigns.length) return null;

  const hasError = campaigns.some(c => c.sendPlan.status === 'paused_error');

  // ── Variante global: franja compacta ──
  if (variant === 'global') {
    const c = campaigns[0];
    const plan = c.sendPlan;
    const multi = campaigns.length > 1;
    return (
      <div className={`mb-4 rounded-lg border px-4 py-2.5 ${
        hasError ? 'bg-red-50 border-red-200' : 'bg-sky-50 border-sky-200'
      }`}>
        <div className="flex items-center gap-3">
          <CalendarClock className={`h-4 w-4 flex-shrink-0 ${hasError ? 'text-red-500' : 'text-sky-600'}`} />
          <div className="flex-1 min-w-0">
            <p className={`text-sm truncate ${hasError ? 'text-red-800' : 'text-sky-800'}`}>
              {multi ? (
                <>📤 <span className="font-medium">{campaigns.length} campañas activas</span>
                  {hasError && <span className="font-semibold"> · una requiere atención</span>}</>
              ) : (
                <>📤 <span className="font-medium">«{c.name}»</span>{c.mode === 'dynamic' && ' 🔄'} · {statusLabel(plan.status)} ·{' '}
                  {plan.totalSent} de {plan.totalQueued} enviados · hoy {sentToday(plan)} de {plan.msgsPerDay}
                  {plan.totalSkipped > 0 && <> · {plan.totalSkipped} omitidos (salieron de la regla)</>}
                  {plan.status === 'running' && etaLabel(plan) && c.mode !== 'dynamic' && <> · termina ~{etaLabel(plan)}</>}</>
              )}
            </p>
            {!multi && (
              <div className="w-full bg-white/70 rounded-full h-1.5 mt-1.5 overflow-hidden">
                <div className={`h-1.5 rounded-full transition-all ${hasError ? 'bg-red-400' : 'bg-sky-500'}`}
                  style={{ width: `${pct(plan)}%` }} />
              </div>
            )}
            {hasError && (
              <p className="text-xs text-red-700 mt-1.5">
                ⚠️ Se pausó sola: revisa que tu WhatsApp siga conectado. Al reconectarse se reanuda.
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/dashboard/campaigns')}
            className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-md border ${
              hasError ? 'border-red-300 text-red-700 hover:bg-red-100' : 'border-sky-300 text-sky-700 hover:bg-sky-100'
            }`}>
            Ver {campaigns.length > 1 ? 'campañas' : 'campaña'}
          </button>
          <button
            onClick={() => { sessionStorage.setItem(DISMISS_KEY, '1'); setDismissed(true); }}
            className="flex-shrink-0 text-content-3 hover:text-content" aria-label="Ocultar">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // ── Variante page: una card por campaña activa ──
  return (
    <div className="space-y-3 mb-5">
      {campaigns.map((c) => {
        const plan = c.sendPlan;
        const isErr = plan.status === 'paused_error';
        return (
          <div key={c.id} className={`rounded-lg border p-4 ${
            isErr ? 'bg-red-50 border-red-200' : 'bg-sky-50 border-sky-200'
          }`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <p className={`text-sm font-medium flex items-center gap-2 ${isErr ? 'text-red-800' : 'text-sky-800'}`}>
                <CalendarClock className="h-4 w-4" />
                «{c.name}» · {statusLabel(plan.status)}
              </p>
              <p className={`text-xs ${isErr ? 'text-red-700' : 'text-sky-700'}`}>
                {plan.msgsPerDay}/día · {plan.window?.start}–{plan.window?.end}
                {plan.status === 'running' && etaLabel(plan) && <> · termina ~{etaLabel(plan)}</>}
              </p>
            </div>
            <div className="flex justify-between text-xs mb-1">
              <span className={isErr ? 'text-red-700' : 'text-sky-700'}>
                {plan.totalSent} de {plan.totalQueued} enviados · hoy {sentToday(plan)} de {plan.msgsPerDay}
              </span>
              <span className={isErr ? 'text-red-700' : 'text-sky-700'}>{pct(plan)}%</span>
            </div>
            <div className="w-full bg-white/70 rounded-full h-2 overflow-hidden">
              <div className={`h-2 rounded-full transition-all ${isErr ? 'bg-red-400' : 'bg-sky-500'}`}
                style={{ width: `${pct(plan)}%` }} />
            </div>
            {isErr && (
              <p className="text-xs text-red-700 mt-2">
                Tu WhatsApp se desconectó o hubo fallos consecutivos. Al reconectarlo, la campaña se reanuda sola.
              </p>
            )}
            {plan.warmup?.active && !isErr && (
              <p className="text-xs text-amber-700 mt-2">
                🌡️ En calentamiento (día {plan.warmup.day + 1} de 7) — hoy máximo {plan.warmup.capToday} mensajes para cuidar tu número nuevo.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ActiveCampaignBanner;
